import { useState, type KeyboardEvent } from 'react';

import { InfoTray } from '@/components/ui/InfoTray';
import type { PlanEvidence, PlanOpportunity, PlanStrength } from '@/api/dashboard';

import styles from './PlanModal.module.css';

/**
 * Shared "plan" presentation used by the Home PlanModal and the Lens
 * complaint Plan tab: narrative, grouped Strengths / Opportunities lists,
 * and the InfoTray that explains a row. One module so the two surfaces can
 * never drift apart again (the Plan tab previously re-implemented the cards
 * against class names that no longer existed).
 */

/** "14" -> "14th". */
export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/** Score -> band, mirroring the PlanService thresholds. */
export function scoreBand(score: number): 'excellent' | 'good' | 'attention' {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  return 'attention';
}

/* Rows
 * --------------------------------------------------------------------- *
 * One grouped list per section (Strengths / Opportunities), hairline-
 * separated rows instead of nested cards. Colour is spent on exactly one
 * thing per row: the score, tinted by its band. Everything else is neutral
 * so the eye goes name -> number -> next row. The clinical "why" lives in
 * the shared InfoTray behind the row's info button, not inline.
 */

/** What the InfoTray shows when a row's info button is pressed. */
export interface RowInfo {
  section: string | null;
  name: string;
  score: number;
  goal: number | null;
  evidence: PlanEvidence | null;
  note: string | null;
  mode: 'strength' | 'gap';
}

/**
 * Eyebrow: "PHYSICAL EXAM · 3 complaints" (section first, spread second).
 * The spread only appears when an indicator genuinely spans complaints; on
 * a single-complaint plan (the Lens Plan tab) every row would say
 * "1 complaint", which is noise.
 */
function Eyebrow({ section, domainLabel, evidence }: { section: string | null | undefined; domainLabel: string; evidence: PlanEvidence | null | undefined }) {
  const n = evidence?.complaintCount ?? 0;
  return (
    <span className={styles.eyebrow}>
      <span className={styles.eyebrowSection}>{section ?? domainLabel}</span>
      {n > 1 && (
        <>
          <span className={styles.eyebrowDot} aria-hidden="true">·</span>
          <span>{n} complaints</span>
        </>
      )}
    </span>
  );
}

/** One quiet line under the name: volume, then where to look first. */
function MetaLine({ evidence, note, score, mode }: { evidence: PlanEvidence | null | undefined; note: string | null; score: number; mode: 'strength' | 'gap' }) {
  if (!evidence) return note ? <p className={styles.meta}>{note}</p> : null;
  const { denom, where, inverted } = evidence;
  const basis = inverted
    ? `Clear on ${score}% of ${denom.toLocaleString()} evaluations`
    : `${score}% of ${denom.toLocaleString()} charts`;
  const whereTxt =
    where.length > 0
      ? `${mode === 'gap' ? 'Most room in' : 'Strongest in'} ${where.join(' and ')}`
      : null;
  return (
    <p className={styles.meta}>
      {basis}
      {whereTxt && (
        <>
          <span className={styles.metaDot} aria-hidden="true">·</span>
          <span className={styles.metaWhere}>{whereTxt}</span>
        </>
      )}
    </p>
  );
}

/**
 * Row interaction. The whole row is the tap target (the pattern settings
 * lists and most product UIs use today), signalled once by a quiet trailing
 * chevron rather than a button stamped on every row. Enter / Space open it
 * from the keyboard; the row carries the accessible name.
 */
function rowPressProps(onOpen: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-haspopup': 'dialog' as const,
    onClick: onOpen,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

function Chevron() {
  return (
    <svg
      className={styles.rowChevron}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3.5 10.5 8 6 12.5" />
    </svg>
  );
}

export function StrengthRow({ strength: s, onInfo }: { strength: PlanStrength; onInfo: (i: RowInfo) => void }) {
  const hasWhy = !!(s.evidence?.why || s.note);
  const open = () =>
    onInfo({
      section: s.section ?? null,
      name: s.name,
      score: s.score,
      goal: null,
      evidence: s.evidence ?? null,
      note: s.note,
      mode: 'strength',
    });
  const press = hasWhy ? rowPressProps(open) : {};
  return (
    <li
      className={hasWhy ? `${styles.row} ${styles.rowTappable}` : styles.row}
      aria-label={hasWhy ? `${s.name}, ${s.score}%. Open details` : undefined}
      {...press}
    >
      <div className={styles.rowMain}>
        <Eyebrow section={s.section} domainLabel={s.domainLabel} evidence={s.evidence} />
        <span className={styles.rowName}>{s.name}</span>
        <MetaLine evidence={s.evidence} note={s.note} score={s.score} mode="strength" />
      </div>
      <div className={styles.rowSide}>
        <span className={`${styles.rowScore} ${styles[`band_${s.band}`]}`}>{s.score}%</span>
        {s.rank != null && s.total != null && (
          <span className={styles.rowSub}>{ordinal(s.rank)} of {s.total}</span>
        )}
      </div>
      {hasWhy && <Chevron />}
    </li>
  );
}

export function OpportunityRow({ opportunity: o, onInfo }: { opportunity: PlanOpportunity; onInfo: (i: RowInfo) => void }) {
  const band = scoreBand(o.score);
  const goalMet = o.score >= o.goal;
  const hasWhy = !!(o.evidence?.why || o.note);
  const open = () =>
    onInfo({
      section: o.section ?? null,
      name: o.name,
      score: o.score,
      goal: o.goal,
      evidence: o.evidence ?? null,
      note: o.note,
      mode: 'gap',
    });
  const press = hasWhy ? rowPressProps(open) : {};
  return (
    <li
      className={hasWhy ? `${styles.row} ${styles.rowTappable}` : styles.row}
      aria-label={hasWhy ? `${o.name}, ${o.score}% of a ${o.goal}% goal. Open details` : undefined}
      {...press}
    >
      <div className={styles.rowMain}>
        <Eyebrow section={o.section} domainLabel={o.domainLabel} evidence={o.evidence} />
        <span className={styles.rowName}>{o.name}</span>
        <MetaLine evidence={o.evidence} note={o.note} score={o.score} mode="gap" />
      </div>
      <div className={styles.rowSide}>
        <span className={`${styles.rowScore} ${styles[`band_${band}`]}`}>{o.score}%</span>
      </div>
      {hasWhy && <Chevron />}
      {/* Progress to goal, full row width, same goal tick as the rest of the
          app, with a footer that states the goal and what reaching it is
          worth in plain words. */}
      <div className={styles.rowTrack} aria-hidden="true">
        <div
          className={`${styles.rowFill} ${goalMet ? styles.fill_surpassed : styles[`fill_${band}`]}`}
          style={{ width: `${Math.min(100, o.score)}%` }}
        />
        <div className={styles.rowGoal} style={{ left: `${Math.min(100, o.goal)}%` }} />
      </div>
      <div className={styles.rowFoot}>
        <span>
          Goal <b>{o.goal}%</b>
          <span className={styles.rowFootGap}>
            {' '}
            · {Math.max(0, o.goal - o.score)} pts to go
          </span>
        </span>
        {o.impactPts > 0 && (
          <span className={styles.rowFootImpact}>
            Reaching it adds about <b>+{o.impactPts} pts</b> to your RSQ®
          </span>
        )}
      </div>
    </li>
  );
}


/* Tray
 * --------------------------------------------------------------------- */

/** The InfoTray body for a plan row: figure, context notes, rationale. */
export function PlanInfoTray({ info, onClose }: { info: RowInfo | null; onClose: () => void }) {
  return (
    <InfoTray
      open={info != null}
      onClose={onClose}
      eyebrow={info?.section ?? 'Indicator'}
      title={info?.name ?? ''}
    >
      {info && (
        <div className={styles.trayBody}>
          <p className={styles.trayFigure}>
            <b className={styles[`band_${scoreBand(info.score)}`]}>{info.score}%</b>
            {info.goal != null && <span className={styles.trayGoal}> · goal {info.goal}%</span>}
            {info.evidence && (
              <span className={styles.trayVolume}>
                {' '}
                · {info.evidence.denom.toLocaleString()}{' '}
                {info.evidence.inverted ? 'evaluations' : 'charts'}
                {info.evidence.complaintCount > 1
                  ? ` across ${info.evidence.complaintCount} complaints`
                  : ''}
              </span>
            )}
          </p>
          {info.evidence?.inverted && (
            <p className={styles.trayNote}>
              Abnormality-detection indicator: the score is the share of visits where the
              abnormality was addressed, shown on the same compliance scale as everything else.
            </p>
          )}
          {info.evidence && info.evidence.where.length > 0 && (
            <p className={styles.trayNote}>
              {info.mode === 'gap' ? 'Most room in' : 'Strongest in'}{' '}
              <b>{info.evidence.where.join(' and ')}</b>.
            </p>
          )}
          <p className={styles.trayWhy}>{info.evidence?.why ?? info.note}</p>
        </div>
      )}
    </InfoTray>
  );
}

/* Sections
 * --------------------------------------------------------------------- */

interface PlanSectionsProps {
  /** Server-generated narrative; may contain <strong> spans. */
  summaryHtml: string;
  strengths: PlanStrength[];
  opportunities: PlanOpportunity[];
  /** Copy for the empty Opportunities state. */
  allAtGoalText?: string;
}

/**
 * Narrative + Strengths + Opportunities with the row InfoTray wired up.
 * Drop into any container; owns its own tray state.
 */
export function PlanSections({
  summaryHtml,
  strengths,
  opportunities,
  allAtGoalText = 'Every indicator is at or above its goal for this window.',
}: PlanSectionsProps) {
  const [info, setInfo] = useState<RowInfo | null>(null);
  return (
    <>
      <p className={styles.perfText} dangerouslySetInnerHTML={{ __html: summaryHtml }} />
      {(strengths.length > 0 || opportunities.length > 0) && (
        <p className={styles.tapHint}>Tap a row for the clinical rationale.</p>
      )}

      {strengths.length > 0 && (
        <section className={styles.section} aria-labelledby="plan-strengths">
          <h3 id="plan-strengths" className={styles.sectionLabel}>
            Strengths
            <span className={styles.sectionHint}>keep doing these</span>
          </h3>
          <ul className={styles.list}>
            {strengths.map((s) => (
              <StrengthRow key={s.id} strength={s} onInfo={setInfo} />
            ))}
          </ul>
        </section>
      )}

      <section className={styles.section} aria-labelledby="plan-opps">
        <h3 id="plan-opps" className={styles.sectionLabel}>
          Opportunities
          <span className={styles.sectionHint}>lowest first</span>
        </h3>
        {opportunities.length === 0 ? (
          <p className={styles.perfText}>{allAtGoalText}</p>
        ) : (
          <ul className={styles.list}>
            {opportunities.map((o) => (
              <OpportunityRow key={o.id} opportunity={o} onInfo={setInfo} />
            ))}
          </ul>
        )}
      </section>

      <PlanInfoTray info={info} onClose={() => setInfo(null)} />
    </>
  );
}
